import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { stories, levels, languages, grammarTopics } from '../data/mock';
import { 
  ArrowLeft, 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  BookOpen, 
  Target,
  Settings,
  Users,
  BarChart3,
  Brain,
  Database,
  Globe,
  Wand2,
  Upload
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Story Management State
  const [selectedStory, setSelectedStory] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [storyForm, setStoryForm] = useState({
    title: '',
    description: '',
    level: 'A1',
    language: 'de',
    content: '',
    readTime: '3 Min',
    image: '',
    grammarFocus: [],
    words: [],
    sentences: []
  });

  // Grammar Topics State
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [newGrammarTopic, setNewGrammarTopic] = useState('');
  const [selectedSentence, setSelectedSentence] = useState('');
  const [sentenceGrammarTopics, setSentenceGrammarTopics] = useState([]);

  const handleStoryEdit = (story) => {
    setSelectedStory(story);
    setStoryForm({
      title: story.title,
      description: story.description,
      level: story.level,
      language: story.language,
      content: story.content,
      readTime: story.readTime,
      image: story.image,
      grammarFocus: story.grammarFocus || [],
      words: story.words || [],
      sentences: story.sentences || []
    });
    setIsEditing(true);
  };

  const handleStoryCreate = () => {
    setSelectedStory(null);
    setStoryForm({
      title: '',
      description: '',
      level: 'A1',
      language: 'de',
      content: '',
      readTime: '3 Min',
      image: '',
      grammarFocus: [],
      words: [],
      sentences: []
    });
    setIsEditing(true);
  };

  const handleStorySave = () => {
    toast({
      title: "Geschichte gespeichert",
      description: `"${storyForm.title}" wurde erfolgreich ${selectedStory ? 'aktualisiert' : 'erstellt'}.`,
    });
    setIsEditing(false);
    setSelectedStory(null);
  };

  const handleStoryDelete = (storyId) => {
    toast({
      title: "Geschichte gelöscht",
      description: "Die Geschichte wurde erfolgreich entfernt.",
      variant: "destructive",
    });
  };

  const handleAddGrammarTopic = () => {
    if (newGrammarTopic.trim()) {
      toast({
        title: "Grammatikthema hinzugefügt",
        description: `"${newGrammarTopic}" wurde zu ${selectedLevel} hinzugefügt.`,
      });
      setNewGrammarTopic('');
    }
  };

  const handleSentenceAnalysis = () => {
    if (selectedSentence.trim() && sentenceGrammarTopics.length > 0) {
      toast({
        title: "Satzanalyse gespeichert",
        description: `Grammatikthemen wurden dem Satz zugeordnet.`,
      });
      setSelectedSentence('');
      setSentenceGrammarTopics([]);
    }
  };

  const addGrammarTopicToSentence = (topic) => {
    if (!sentenceGrammarTopics.includes(topic)) {
      setSentenceGrammarTopics([...sentenceGrammarTopics, topic]);
    }
  };

  const removeGrammarTopicFromSentence = (topic) => {
    setSentenceGrammarTopics(sentenceGrammarTopics.filter(t => t !== topic));
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-100 text-green-800',
      'A2': 'bg-blue-100 text-blue-800',
      'B1': 'bg-yellow-100 text-yellow-800',
      'B2': 'bg-orange-100 text-orange-800',
      'C1': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/profile')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Settings className="h-6 w-6 text-[#4ECDC4]" />
                <h1 className="text-xl font-bold">Admin Panel</h1>
              </div>
            </div>
            <Badge className="bg-[#4ECDC4] text-white">
              DMV-Lernportal
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="stories" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="stories" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Geschichten
            </TabsTrigger>
            <TabsTrigger value="grammar" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Grammatik
            </TabsTrigger>
            <TabsTrigger value="words" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Wörterdatenbank
            </TabsTrigger>
            <TabsTrigger value="generator" className="flex items-center gap-2">
              <Wand2 className="h-4 w-4" />
              Story-Generator
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Statistiken
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Benutzer
            </TabsTrigger>
          </TabsList>

          {/* Stories Management */}
          <TabsContent value="stories" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Geschichten verwalten</h2>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={handleStoryCreate}
              >
                <Plus className="h-4 w-4 mr-2" />
                Neue Geschichte
              </Button>
            </div>

            {isEditing ? (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>
                    {selectedStory ? 'Geschichte bearbeiten' : 'Neue Geschichte erstellen'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Titel</label>
                      <Input
                        value={storyForm.title}
                        onChange={(e) => setStoryForm({...storyForm, title: e.target.value})}
                        placeholder="Geschichtentitel"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Lesezeit</label>
                      <Input
                        value={storyForm.readTime}
                        onChange={(e) => setStoryForm({...storyForm, readTime: e.target.value})}
                        placeholder="z.B. 3 Min"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Beschreibung</label>
                    <Textarea
                      value={storyForm.description}
                      onChange={(e) => setStoryForm({...storyForm, description: e.target.value})}
                      placeholder="Kurze Beschreibung der Geschichte"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Niveau</label>
                      <Select value={storyForm.level} onValueChange={(value) => setStoryForm({...storyForm, level: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {levels.map((level) => (
                            <SelectItem key={level.code} value={level.code}>
                              {level.code} - {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Sprache</label>
                      <Select value={storyForm.language} onValueChange={(value) => setStoryForm({...storyForm, language: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map((lang) => (
                            <SelectItem key={lang.code} value={lang.code}>
                              {lang.flag} {lang.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Bild URL</label>
                      <Input
                        value={storyForm.image}
                        onChange={(e) => setStoryForm({...storyForm, image: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Geschichteninhalt</label>
                    <Textarea
                      value={storyForm.content}
                      onChange={(e) => setStoryForm({...storyForm, content: e.target.value})}
                      placeholder="Der vollständige Text der Geschichte..."
                      rows={10}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                      onClick={handleStorySave}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Speichern
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stories.map((story) => (
                  <Card key={story.id} className="border-0 shadow-lg overflow-hidden">
                    <div className="aspect-video relative">
                      <img 
                        src={story.image} 
                        alt={story.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <Badge className={getLevelColor(story.level)}>
                          {story.level}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-slate-900 mb-2 line-clamp-2">
                        {story.title}
                      </h3>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                        {story.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                          <span>{story.language.toUpperCase()}</span>
                          <span>•</span>
                          <span>{story.readTime}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStoryEdit(story)}
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStoryDelete(story.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Grammar Management */}
          <TabsContent value="grammar" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Grammatikthemen verwalten</h2>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={() => navigate('/admin/grammar')}
              >
                <Brain className="h-4 w-4 mr-2" />
                Erweiterte Grammatik-Verwaltung
              </Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Grammar Topics by Level */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Grammatikthemen nach Niveau</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level.code} value={level.code}>
                          {level.code} - {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="space-y-2">
                    {grammarTopics[selectedLevel]?.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm">{topic}</span>
                        <Button size="sm" variant="ghost">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="flex gap-2">
                    <Input
                      placeholder="Neues Grammatikthema"
                      value={newGrammarTopic}
                      onChange={(e) => setNewGrammarTopic(e.target.value)}
                    />
                    <Button onClick={handleAddGrammarTopic}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sentence Grammar Mapping */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Satz-Grammatik Zuordnung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Satz oder Satzglied</label>
                    <Textarea
                      placeholder="Geben Sie den Satz ein, dem Sie Grammatikthemen zuordnen möchten..."
                      value={selectedSentence}
                      onChange={(e) => setSelectedSentence(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Verfügbare Grammatikthemen</label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {grammarTopics[selectedLevel]?.map((topic, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="cursor-pointer hover:bg-[#4ECDC4] hover:text-white"
                          onClick={() => addGrammarTopicToSentence(topic)}
                        >
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {sentenceGrammarTopics.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium mb-2">Zugeordnete Themen</label>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {sentenceGrammarTopics.map((topic, index) => (
                          <Badge
                            key={index}
                            className="bg-[#4ECDC4] text-white cursor-pointer"
                            onClick={() => removeGrammarTopicFromSentence(topic)}
                          >
                            {topic} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                    onClick={handleSentenceAnalysis}
                    disabled={!selectedSentence.trim() || sentenceGrammarTopics.length === 0}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Zuordnung speichern
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Word Database Management */}
          <TabsContent value="words" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Wörterdatenbank</h2>
                <p className="text-slate-600 mt-1">
                  Verwalten Sie alle Wörter mit Schlüsselwort-Methode und visuellen Merkhilfen
                </p>
              </div>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={() => navigate('/admin/words')}
              >
                <Database className="h-4 w-4 mr-2" />
                Erweiterte Wörterverwaltung
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Quick Stats */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Schnellübersicht</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Gesamt Wörter</span>
                    <Badge className="bg-[#4ECDC4] text-white">2,456</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Mit Merksätzen</span>
                    <Badge variant="outline">1,234 (50%)</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Mit Bildern</span>
                    <Badge variant="outline">856 (35%)</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Sprachen</span>
                    <Badge variant="secondary">6</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Words */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Zuletzt hinzugefügte Wörter</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">vergessen</div>
                      <div className="text-sm text-slate-500">Verb • Deutsch</div>
                    </div>
                    <Badge variant="outline" className="text-xs">Neu</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Bibliothek</div>
                      <div className="text-sm text-slate-500">Substantiv • Deutsch</div>
                    </div>
                    <Badge variant="secondary" className="text-xs">Merksatz ✓</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">beautiful</div>
                      <div className="text-sm text-slate-500">Adjektiv • English</div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-800 text-xs">Bild ✓</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">lächeln</div>
                      <div className="text-sm text-slate-500">Verb • Deutsch</div>
                    </div>
                    <Badge className="bg-green-100 text-green-800 text-xs">Vollständig</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Schnellaktionen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/admin/words')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Neues Wort hinzufügen
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    Automatische Übersetzung
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    Wörter aus Story extrahieren
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Lernfortschritt anzeigen
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Language Distribution */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Wörter nach Sprachen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#4ECDC4]">1,245</div>
                    <div className="text-sm text-slate-600">Deutsch</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-500">456</div>
                    <div className="text-sm text-slate-600">English</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">321</div>
                    <div className="text-sm text-slate-600">Français</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">234</div>
                    <div className="text-sm text-slate-600">Español</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500">156</div>
                    <div className="text-sm text-slate-600">Italiano</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-500">44</div>
                    <div className="text-sm text-slate-600">Türkçe</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Story Generator Management */}
          <TabsContent value="generator" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Story-Generator</h2>
                <p className="text-slate-600 mt-1">
                  Automatische Story-Generierung für verschiedene Sprachlevel
                </p>
              </div>
              <Button 
                className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                onClick={() => navigate('/admin/story-generator')}
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Story-Generator öffnen
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Generator Stats */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Generator-Statistiken</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Generierte Stories</span>
                    <Badge className="bg-[#4ECDC4] text-white">24</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Aktive Level</span>
                    <Badge variant="outline">6</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Durchschnitt. Qualität</span>
                    <Badge variant="secondary">4.2/5</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Heute generiert</span>
                    <Badge className="bg-green-100 text-green-800">3</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Level Readiness */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Level-Bereitschaft</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">A1 - Anfänger</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '90%'}}></div>
                      </div>
                      <span className="text-xs text-green-600">90%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">A2 - Grundkenntnisse</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '85%'}}></div>
                      </div>
                      <span className="text-xs text-green-600">85%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">B1 - Mittelstufe</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{width: '65%'}}></div>
                      </div>
                      <span className="text-xs text-yellow-600">65%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">B2 - Selbstständig</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{width: '60%'}}></div>
                      </div>
                      <span className="text-xs text-yellow-600">60%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">C1 - Fortgeschritten</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{width: '30%'}}></div>
                      </div>
                      <span className="text-xs text-red-600">30%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">C2 - Fachkundig</span>
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{width: '20%'}}></div>
                      </div>
                      <span className="text-xs text-red-600">20%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Schnellaktionen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/admin/story-generator')}
                  >
                    <Wand2 className="h-4 w-4 mr-2" />
                    Neue Story generieren
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Level-Analyse anzeigen
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => navigate('/admin/words')}
                  >
                    <Database className="h-4 w-4 mr-2" />
                    Wörterdatenbank erweitern
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Bulk-Story Upload
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Recent Generated Stories */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Zuletzt generierte Stories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Die kleine Katze</div>
                      <div className="text-sm text-gray-500">A1 • Deutsch • 156 Wörter</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-green-500 text-white">A1</Badge>
                      <Badge variant="outline" className="text-xs">4.5/5</Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Mein erster Tag</div>
                      <div className="text-sm text-gray-500">A2 • Deutsch • 203 Wörter</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-green-600 text-white">A2</Badge>
                      <Badge variant="outline" className="text-xs">4.2/5</Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">Das Wetter heute</div>
                      <div className="text-sm text-gray-500">B1 • Deutsch • 289 Wörter</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-yellow-500 text-white">B1</Badge>
                      <Badge variant="outline" className="text-xs">3.8/5</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics" className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Statistiken</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <BookOpen className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{stories.length}</div>
                  <div className="text-sm text-slate-600">Geschichten</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <Users className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">1,247</div>
                  <div className="text-sm text-slate-600">Aktive Benutzer</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">15,823</div>
                  <div className="text-sm text-slate-600">Gelernte Wörter</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <BarChart3 className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">98.2%</div>
                  <div className="text-sm text-slate-600">Uptime</div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Beliebteste Geschichten</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stories.slice(0, 5).map((story, index) => (
                    <div key={story.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-[#4ECDC4] rounded-full flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{story.title}</div>
                          <div className="text-sm text-slate-600">{story.level} • {story.language.toUpperCase()}</div>
                        </div>
                      </div>
                      <div className="text-sm text-slate-600">
                        {Math.floor(Math.random() * 500) + 100} Aufrufe
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Benutzerverwaltung</h2>
            
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Users className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Benutzerverwaltung in Entwicklung
                </h3>
                <p className="text-slate-600">
                  Diese Funktion wird in einer zukünftigen Version verfügbar sein.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;